import './styles.scss'

import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Input, Radio } from 'antd'
import { clsx } from 'clsx'

import { useAppSelector } from '@/store/hook'
import { selectCountryNames } from '@/store/selectors/configSelector'
import { selectUserCountry } from '@/store/selectors/userSelector'
import useRegion from '@/hooks/useRegion'

import NewDeliveryAddress from '../NewDeliveryAddress'

const MyDeliveryAddressHidden = ({ isShowRegion = false }) => {
  return (
    <>
      <Form.Item hidden name="myAddrPartnerNumber">
        <Input />
      </Form.Item>
      <Form.Item hidden name="myAddrName">
        <Input />
      </Form.Item>
      <Form.Item hidden name="myAddrStreet">
        <Input />
      </Form.Item>
      <Form.Item hidden name="myAddrStreet4">
        <Input />
      </Form.Item>
      <Form.Item hidden name="myAddrName2">
        <Input />
      </Form.Item>
      <Form.Item hidden name="myAddrHouseNumber">
        <Input />
      </Form.Item>
      <Form.Item hidden name="myAddrZip">
        <Input />
      </Form.Item>
      {isShowRegion && (
        <Form.Item hidden name="myAddrRegion">
          <Input />
        </Form.Item>
      )}
      <Form.Item hidden name="myAddrCity">
        <Input />
      </Form.Item>
      <Form.Item hidden name="myAddrCountry">
        <Input />
      </Form.Item>
      <Form.Item hidden name="myAddrPhoneNumber">
        <Input />
      </Form.Item>
      <Form.Item hidden name="myAddrEmail">
        <Input />
      </Form.Item>
    </>
  )
}

const radioStyle = {
  display: 'block',
  height: '30px',
  lineHeight: '22px',
}

interface DeliveryAddressMobileProps {
  deliveryAddresses: unknown[]
  form: {
    setFieldsValue?: (...args: unknown[]) => unknown
  }
}

const DeliveryAddressMobile = ({
  deliveryAddresses,
  form,
}: DeliveryAddressMobileProps) => {
  const { t } = useTranslation()
  const [isAddNewAddress, setIsAddNewAddress] = useState(false)
  const country = useAppSelector(selectUserCountry)
  const countryNames = useAppSelector(selectCountryNames)
  const { getAddressWithRegion, isShowRegion } = useRegion()

  const handleSelectMyAddress = (selectedId) => {
    const address = deliveryAddresses[selectedId]

    setIsAddNewAddress(false)
    form.setFieldsValue?.({
      myAddrPartnerNumber: address?.partnerNumber,
      myAddrName: address?.name,
      myAddrName2: address?.name2,
      myAddrStreet: address?.street,
      myAddrStreet4: address?.street4,
      myAddrHouseNumber: address?.houseNumber,
      myAddrZip: address?.zip,
      myAddrRegion: address?.region,
      myAddrCity: address?.city,
      myAddrCountry: address?.country,
      myAddrPhoneNumber: address?.phoneNumber,
      myAddrEmail: address?.email,
      currentDeliveryAddr: 1,
    })
  }

  const handleSelectAddNewAddress = () => {
    setIsAddNewAddress(true)
    form.setFieldsValue?.({
      newAddrPartnerNumber: '',
      newAddrName: '',
      newAddrName2: '',
      newAddrStreet: '',
      newAddrStreet4: '',
      newAddrHouseNumber: '',
      newAddrZip: '',
      newAddrRegion: '',
      newAddrCity: '',
      newAddrPhoneNumber: '',
      newAddrCountry: country,
      newAddrEmail: '',
      currentDeliveryAddr: 2,
    })
  }

  return (
    <Radio.Group
      className="custom-input"
      defaultValue={0}
      style={{ width: '100%' }}
      onChange={(e) => {
        const { value } = e.target

        if (value === 'newAddress') {
          handleSelectAddNewAddress()
        } else {
          handleSelectMyAddress(value)
        }
      }}
    >
      {deliveryAddresses?.map((data, index) => {
        const address = getAddressWithRegion(data)
        const countryName = countryNames.find(
          (countryNameConfig) => countryNameConfig.value === address.country,
        )?.name

        return (
          <Radio
            key={data.partnerNumber}
            className="delivery-information-radio border-bottom"
            value={index}
          >
            <div
              className={clsx(
                'delivery-information',
                isAddNewAddress && 'disabled',
              )}
            >
              <span className="delivery-information__name font-weight-bold">
                {address.name}
              </span>
              <span className="delivery-information__address">
                {address.address}
              </span>
              <span className="delivery-information__address">
                {address.street4}
              </span>
              <span className="delivery-information__zip">
                {address.location}
              </span>
              <span className="delivery-information__country font-weight-bold">
                {countryName}
              </span>
            </div>
          </Radio>
        )
      })}
      <Radio
        className="delivery-information-radio border-bottom"
        style={radioStyle}
        value="newAddress"
      >
        <span className="mt-6 mb-4 delivery__option__title">
          {t('form.label.newAddress')}
        </span>
        {isAddNewAddress
          ? <NewDeliveryAddress form={form} />
          : <MyDeliveryAddressHidden isShowRegion={isShowRegion} />}
      </Radio>
    </Radio.Group>
  )
}

export default memo(DeliveryAddressMobile)
